/**
 * ThemeContext - Gestiona el tema global de la aplicación
 * Proporciona el tema actual y funciones para cambiarlo
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, ThemeMode, themes } from '../themes/theme';
import logger from '../utils/simpleLogger';

interface ThemeContextType {
  theme: Theme;
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Obtener tema guardado en localStorage, o detectar preferencia del sistema, o usar 'dark'
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode');
    if (saved === 'light' || saved === 'dark') {
      document.documentElement.setAttribute('data-theme', saved);
      return saved;
    }
    // No saved preference — default to dark
    const initialTheme: ThemeMode = 'dark';
    document.documentElement.setAttribute('data-theme', initialTheme);
    return initialTheme;
  });

  const theme = themes[themeMode];

  // Guardar preferencia en localStorage y aplicar data-theme al HTML
  useEffect(() => {
    localStorage.setItem('theme-mode', themeMode);
    document.documentElement.setAttribute('data-theme', themeMode);
    logger.info(`🎨 Tema cambiado a: ${themeMode}`);
  }, [themeMode]);

  const toggleTheme = () => {
    setThemeMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const value = {
    theme,
    themeMode,
    toggleTheme,
    setThemeMode,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Hook personalizado para usar el tema
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme debe usarse dentro de un ThemeProvider');
  }
  return context;
};
